<template>
  <div class="coupon-rules">
    <!-- 头部设置车场按钮 -->
    <div class="header-section">
      <div class="header-title">
        <h3>优惠券领取规则配置</h3>
      </div>
      <div class="header-actions">
        <el-button type="primary" @click="showParkingDialog">
          <i class="el-icon-setting"></i>
          设置车场
        </el-button>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 左右分栏布局 -->
      <div class="config-layout">
        <!-- 左侧：基础配置 -->
        <div class="left-config">
          <div class="config-section">
            <h3 class="section-title">
              <i class="el-icon-setting"></i>
              基础配置
            </h3>
            <div class="config-item vertical-layout">
              <label class="config-label">
                <i class="el-icon-time"></i>
                领取有效期
              </label>
              <div class="config-input">
                <div class="config-input-row">
                  <el-input-number
                    v-model="couponConfig.validDays"
                    :min="1"
                    :max="365"
                    :step="1"
                    :precision="0"
                    placeholder="请输入有效期天数"
                    size="large"
                    controls-position="right"
                    class="custom-input-number"
                  ></el-input-number>
                  <span class="unit">天</span>
                </div>
                <div class="input-tips">
                  <span class="tip-text">建议设置7-30天</span>
                </div>
              </div>
            </div>

            <div class="config-item vertical-layout">
              <label class="config-label">
                <i class="el-icon-s-order"></i>
                默认领取次数
              </label>
              <div class="config-input">
                <div class="config-input-row">
                  <el-input-number
                    v-model="couponConfig.defaultLimit"
                    :min="1"
                    :max="999"
                    :step="1"
                    :precision="0"
                    placeholder="请输入默认次数"
                    size="large"
                    controls-position="right"
                    class="custom-input-number"
                  ></el-input-number>
                  <span class="unit">次</span>
                </div>
                <div class="input-tips">
                  <span class="tip-text">建议设置1-5次</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧：多时段配置 -->
        <div class="right-config">
          <div class="time-period-section">
            <div class="section-header">
              <div class="header-left">
                <h4>
                  <i class="el-icon-time"></i>
                  多时段特定次数配置
                </h4>
                <span> ({{ timePeriods.length }}/3) </span>
                <span>*最多可配置三组数据</span>
              </div>
              <el-button
                type="primary"
                size="small"
                @click="addTimePeriod"
                :disabled="timePeriods.length >= 3"
              >
                <i class="el-icon-plus"></i>
                添加时段
              </el-button>
            </div>

            <div class="time-period-container">
              <div class="time-period-list" ref="timePeriodList">
                <div
                  v-for="(period, index) in timePeriods"
                  :key="index"
                  class="time-period-item"
                >
                  <div class="period-time">
                    <el-time-picker
                      v-model="period.startTime"
                      format="HH:mm"
                      value-format="HH:mm"
                      placeholder="开始时间"
                      size="small"
                      class="time-picker"
                    ></el-time-picker>
                    <span class="time-separator">-</span>
                    <el-time-picker
                      v-model="period.endTime"
                      format="HH:mm"
                      value-format="HH:mm"
                      placeholder="结束时间"
                      size="small"
                      class="time-picker"
                    ></el-time-picker>
                  </div>
                  <div class="period-count">
                    <el-input-number
                      v-model="period.limitCount"
                      :min="1"
                      :max="999"
                      size="small"
                      placeholder="次数"
                      class="count-input"
                    ></el-input-number>
                    <span class="unit">次</span>
                  </div>
                  <div class="period-actions">
                    <el-button
                      type="danger"
                      size="mini"
                      icon="el-icon-delete"
                      @click="removeTimePeriod(index)"
                      circle
                    ></el-button>
                  </div>
                </div>
              </div>

              <!-- 空状态提示 -->
              <div v-if="timePeriods.length === 0" class="empty-state">
                <i class="el-icon-time"></i>
                <p>暂无时段配置，点击上方"添加时段"按钮开始配置</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 保存按钮 -->
      <div class="save-section">
        <el-button type="primary" size="large" @click="saveConfig">
          <i class="el-icon-check"></i>
          保存配置
        </el-button>
      </div>
    </div>

    <!-- 车场设置弹窗 -->
    <el-drawer
      title="车场设置"
      :visible.sync="showParkingDrawer"
      direction="rtl"
      size="600px"
      @close="closeParkingDrawer"
    >
      <div class="parking-drawer">
        <!-- 道路分类 -->
        <div class="parking-category">
          <h4 class="category-title">
            <i class="el-icon-road"></i>
            道路
          </h4>
          <div class="parking-list">
            <div v-for="road in roadList" :key="road.id" class="parking-item">
              <div class="parking-info">
                <span class="parking-name">{{ road.name }}</span>
                <span
                  class="parking-status"
                  :class="road.status === 1 ? 'active' : 'inactive'"
                >
                  {{ road.status === 1 ? "启用" : "禁用" }}
                </span>
              </div>
              <el-switch
                v-model="road.status"
                :active-value="1"
                :inactive-value="0"
                @change="toggleParkingStatus(road)"
              ></el-switch>
            </div>
          </div>
        </div>

        <!-- 车场分类 -->
        <div class="parking-category">
          <h4 class="category-title">
            <i class="el-icon-office-building"></i>
            车场
          </h4>
          <div class="parking-list">
            <div
              v-for="parking in parkingList"
              :key="parking.id"
              class="parking-item"
            >
              <div class="parking-info">
                <span class="parking-name">{{ parking.name }}</span>
                <span
                  class="parking-status"
                  :class="parking.status === 1 ? 'active' : 'inactive'"
                >
                  {{ parking.status === 1 ? "启用" : "禁用" }}
                </span>
              </div>
              <el-switch
                v-model="parking.status"
                :active-value="1"
                :inactive-value="0"
                @change="toggleParkingStatus(parking)"
              ></el-switch>
            </div>
          </div>
        </div>

        <!-- 共享车场分类 -->
        <div class="parking-category">
          <h4 class="category-title">
            <i class="el-icon-share"></i>
            共享车场
          </h4>
          <div class="parking-list">
            <div
              v-for="shared in sharedParkingList"
              :key="shared.id"
              class="parking-item shared-parking"
            >
              <div class="parking-info">
                <div class="parking-name-row">
                  <span class="parking-name">{{ shared.name }}</span>
                  <span
                    class="parking-status"
                    :class="shared.status === 1 ? 'active' : 'inactive'"
                  >
                    {{ shared.status === 1 ? "启用" : "禁用" }}
                  </span>
                </div>
                <span
                  class="parking-type"
                  :class="shared.type === 1 ? 'type-first' : 'type-second'"
                >
                  {{ shared.type == 2 ? "一类车场" : "二类车场" }}
                </span>
              </div>
              <el-switch
                v-model="shared.status"
                :active-value="1"
                :inactive-value="0"
                @change="toggleParkingStatus(shared)"
              ></el-switch>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import Page from "@/components/commons/BaseList.vue";
import {
  shopCouponRule,
  shopCouponRuleEdit,
  allParkingList,
  couponSwitch,
} from "@/api/url";

export default {
  name: "coupon-rules",
  components: {
    Page,
  },
  data() {
    return {
      moduleName: "coupon-rules",
      // 优惠券配置
      couponConfig: {
        validDays: 7,
        defaultLimit: 3,
      },
      // 多时段配置
      timePeriods: [],
      // 车场设置弹窗
      showParkingDrawer: false,
      // 道路列表
      roadList: [
        { id: 1, name: "中山路", status: 1 },
        { id: 2, name: "解放路", status: 0 },
        { id: 3, name: "人民路", status: 1 },
        { id: 4, name: "建设路", status: 1 },
        { id: 5, name: "文化路", status: 0 },
      ],
      // 车场列表
      parkingList: [
        { id: 1, name: "万达广场停车场", status: 1 },
        { id: 2, name: "银泰城停车场", status: 1 },
        { id: 3, name: "万象城停车场", status: 0 },
        { id: 4, name: "大悦城停车场", status: 1 },
      ],
      // 共享车场列表
      sharedParkingList: [
        { id: 1, name: "共享停车场A", status: 1, type: 1 },
        { id: 2, name: "共享停车场B", status: 0, type: 2 },
        { id: 3, name: "共享停车场C", status: 1, type: 1 },
        { id: 4, name: "共享停车场D", status: 1, type: 2 },
      ],
    };
  },

  mounted() {
    // 页面加载时初始化数据
    this.loadConfig();
    this.getAllParkingList();
  },
  methods: {
    ...mapMutations(["getPage"]),
    getAllParkingList() {
      this.$get(allParkingList()).then((res) => {
        if (res) {
          const allParkingData = res || [];
          this.classifyParkingData(allParkingData);
        } else {
          this.$message.error(res.message || "获取车场列表失败");
        }
      });
    },

    // 根据接口返回数据按类型分类
    classifyParkingData(data) {
      // 初始化分类数组
      this.roadList = [];
      this.parkingList = [];
      this.sharedParkingList = [];

      // 遍历数据并根据类型分类
      data.forEach((item) => {
        const parkingItem = {
          id: item.parkingId,
          name: item.parkingName,
          status: item?.attr4 == "y" ? 1 : 0, // 根据attr4字段判断状态，y为启用
        };

        // 根据 isShare 和 parkingType 字段判断分类
        if (item.isShare == 1) {
          // 共享车场类型
          const sharedItem = {
            ...parkingItem,
            type: item.attr2 || "1", // 默认为一类车场,1类企业车位、2类个人车位
          };
          this.sharedParkingList.push(sharedItem);
        } else if (item.parkingType == "onsite") {
          // 普通车场类型
          this.parkingList.push(parkingItem);
        } else if (item.parkingType == "roadside") {
          // 道路类型
          this.roadList.push(parkingItem);
        }
      });

      console.log("分类后的数据:", {
        roadList: this.roadList,
        parkingList: this.parkingList,
        sharedParkingList: this.sharedParkingList,
      });
    },
    // 加载配置数据
    async loadConfig() {
      // API加载配置数据
      const config = await this.$get(shopCouponRule());
      this.couponConfig = config?.data || {};
      this.timePeriods = config?.data?.couponTimeRulesList || [];
    },

    // 显示车场设置弹窗
    showParkingDialog() {
      this.showParkingDrawer = true;
    },

    // 关闭车场设置弹窗
    closeParkingDrawer() {
      this.showParkingDrawer = false;
    },

    // 切换车场状态
    toggleParkingStatus(item) {
      console.log("切换车场状态:", item);
      this.$putRequest(`${couponSwitch()}`, {
        parkingId: item.id,
        attr4: item.status === 1 ? "y" : "n",
      }).then((res) => {
        if (res.status !== 200) {
          this.$message.error(
            res.message ||
              `${item.name} 状态${item.status === 1 ? "启用" : "禁用"}失败`
          );
          return;
        }
        this.$message.success(
          `${item.name} 状态已${item.status === 1 ? "启用" : "禁用"}`
        );
        this.showParkingDrawer = false;
        this.getAllParkingList();
      });
    },

    // 添加时段·
    addTimePeriod() {
      if (this.timePeriods.length >= 3) {
        this.$message.warning("最多只能设置3组时段配置");
        return;
      }

      const newId = Math.max(...this.timePeriods.map((p) => p.id)) + 1;
      this.timePeriods.push({
        id: newId,
        startTime: "",
        endTime: "",
        count: 1,
      });
    },

    // 删除时段
    removeTimePeriod(index) {
      this.timePeriods.splice(index, 1);
    },

    // 保存配置
    saveConfig() {
      const config = {
        ...this.couponConfig,
        couponTimeRulesList: [...this.timePeriods],
      };
      //调用API保存配置
      this.$putRequest(shopCouponRuleEdit(), config).then((res) => {
        if (res.status !== 200) {
          this.$message.error(res.message || "保存失败");
          return;
        }
        this.$message.success("保存成功");
      });
    },
  },
};
</script>

<style scoped>
.coupon-rules {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}

/* 头部样式 */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 10px 20px;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-title h3 {
  margin: 0;
  color: #333;
  font-size: 20px;
}

/* 主要内容区域 */
.main-content {
  background: white;
  border-radius: 8px;
  padding: 10px 30px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 左右分栏布局 */
.config-layout {
  display: flex;
  gap: 20px;
  margin-top: 20px;
}

.left-config {
  flex: 0 0 400px;
  min-width: 400px;
}

.right-config {
  flex: 1;
  min-width: 0;
}

/* 配置区域 */
.config-section {
  margin-bottom: 10px;
  background: #fafbfc;
  padding: 10px 25px;
  border-radius: 8px;
  border: 1px solid #e8eaed;
}

.section-title {
  margin: 0 0 20px 0;
  padding: 10px 0;
  border-bottom: 2px solid #409eff;
  color: #333;
  font-size: 18px;
  display: flex;
  align-items: center;
}

.section-title i {
  margin-right: 8px;
  color: #409eff;
  font-size: 20px;
}

.config-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 25px;
  padding: 15px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e6e6e6;
  transition: all 0.3s ease;
}

.config-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.config-item:last-child {
  margin-bottom: 0;
}

.config-item.vertical-layout {
  flex-direction: column;
  align-items: stretch;
}

.config-item.vertical-layout .config-label {
  width: auto;
  margin-right: 0;
  margin-bottom: 15px;
  text-align: left;
}

.config-label {
  width: 180px;
  font-size: 16px;
  color: #333;
  font-weight: 600;
  display: flex;
  align-items: center;
  margin-right: 20px;
}

.config-label i {
  margin-right: 8px;
  color: #409eff;
  font-size: 18px;
}

.config-input {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.config-input-row {
  display: flex;
  align-items: center;
}

.config-input .unit {
  margin-left: 12px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  background: #f0f2f5;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
}

.input-tips {
  margin-left: 15px;
  margin-top: 8px;
}

.config-item.vertical-layout .input-tips {
  margin-left: 0;
  margin-top: 8px;
}

.tip-text {
  font-size: 12px;
  color: #999;
  background: #f0f9ff;
  padding: 4px 8px;
  border-radius: 4px;
  border-left: 3px solid #409eff;
  white-space: nowrap;
}

/* 自定义input-number样式 */
.custom-input-number {
  width: 200px;
}

.custom-input-number .el-input__inner {
  height: 40px;
  line-height: 40px;
  font-size: 16px;
  border: 2px solid #e6e6e6;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.custom-input-number .el-input__inner:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

.custom-input-number .el-input-number__increase,
.custom-input-number .el-input-number__decrease {
  width: 32px;
  height: 20px;
  line-height: 20px;
  border: none;
  background: #f5f7fa;
  color: #606266;
  transition: all 0.3s ease;
}

.custom-input-number .el-input-number__increase:hover,
.custom-input-number .el-input-number__decrease:hover {
  background: #409eff;
  color: white;
}

/* 垂直布局中的input样式调整 */
.config-item.vertical-layout .custom-input-number {
  width: 100%;
  max-width: 300px;
}

.config-item.vertical-layout .config-input-row {
  justify-content: flex-start;
  align-items: center;
}

.config-item.vertical-layout .unit {
  margin-left: 12px;
  margin-right: 0;
}

/* 确保input框在垂直布局中正确显示 */
.config-item.vertical-layout .el-input-number {
  display: inline-block;
}

.config-item.vertical-layout .el-input-number .el-input {
  width: 100%;
}

.config-item.vertical-layout .el-input-number .el-input__inner {
  width: 100%;
  box-sizing: border-box;
}

/* 多时段配置区域 */
.time-period-section {
  margin-bottom: 20px;
  background: #fafbfc;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e8eaed;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #409eff;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.period-count-info {
  font-size: 14px;
  color: #666;
  background: #f0f2f5;
  padding: 2px 8px;
  border-radius: 12px;
  border: 1px solid #d9d9d9;
}

.section-header h4 {
  margin: 0;
  color: #333;
  font-size: 18px;
  display: flex;
  align-items: center;
}

.section-header h4 i {
  margin-right: 8px;
  color: #409eff;
  font-size: 20px;
}

.time-period-container {
  flex: 1;
  min-height: 300px;
  position: relative;
  display: flex;
  flex-direction: column;
}

.time-period-list {
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  overflow: hidden;
  background: white;
  flex: 1;
  min-height: 200px;
  transition: all 0.3s ease;
}

.time-period-item {
  display: flex;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
  background: white;
  transition: all 0.3s ease;
}

.time-period-item:hover {
  background: #f8f9ff;
  border-color: #409eff;
}

.time-period-item:last-child {
  border-bottom: none;
}

.period-time {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.time-picker {
  width: 120px;
}

.time-picker .el-input__inner {
  height: 32px;
  line-height: 32px;
  font-size: 14px;
}

.time-separator {
  margin: 0 15px;
  color: #666;
  font-weight: bold;
  font-size: 16px;
}

.period-count {
  display: flex;
  align-items: center;
  margin: 0 25px;
  min-width: 120px;
}

.count-input {
  width: 100px;
}

.count-input .el-input__inner {
  height: 32px;
  line-height: 32px;
  font-size: 14px;
}

.period-count .unit {
  margin-left: 8px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

.period-actions {
  width: 50px;
  text-align: center;
}

/* 空状态样式 */
.empty-state {
  text-align: center;
  padding: 30px 20px;
  color: #999;
  background: white;
  border: 2px dashed #e6e6e6;
  border-radius: 6px;
}

.empty-state i {
  font-size: 40px;
  color: #ddd;
  margin-bottom: 10px;
  display: block;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .config-layout {
    flex-direction: column;
    gap: 15px;
  }

  .left-config {
    flex: none;
    min-width: auto;
  }

  .right-config {
    flex: none;
  }

  .config-item.vertical-layout .custom-input-number {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .config-item.vertical-layout .custom-input-number {
    width: 100%;
    max-width: 100%;
  }

  .config-item.vertical-layout .config-input-row {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .config-item.vertical-layout .unit {
    margin-left: 0;
    text-align: center;
    align-self: center;
  }

  .time-period-item {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
  }

  .period-time {
    margin-bottom: 12px;
    justify-content: center;
  }

  .period-count {
    margin: 0 0 12px 0;
    justify-content: center;
  }

  .period-actions {
    width: 100%;
    text-align: center;
  }

  .time-picker {
    width: 100px;
  }

  .count-input {
    width: 80px;
  }
}

@media (max-width: 480px) {
  .time-period-section {
    padding: 12px;
  }

  .section-header {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    margin-bottom: 12px;
  }

  .section-header h4 {
    text-align: center;
  }

  .time-picker {
    width: 90px;
  }

  .count-input {
    width: 70px;
  }
}

/* 保存区域 */
.save-section {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 20px;
  margin-top: 10px;
  box-sizing: border-box;
  border-top: 1px solid #e6e6e6;
}

/* 车场设置弹窗样式 */
.parking-drawer {
  padding: 20px 0;
}

.parking-category {
  margin-bottom: 30px;
}

.category-title {
  display: flex;
  align-items: center;
  margin: 0 0 15px 0;
  padding: 10px 0;
  border-bottom: 2px solid #409eff;
  color: #333;
  font-size: 16px;
}

.category-title i {
  margin-right: 8px;
  color: #409eff;
}

.parking-list {
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  overflow: hidden;
}

.parking-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
  background: white;
}

.parking-item:last-child {
  border-bottom: none;
}

.parking-item.shared-parking {
  background: #f8f9ff;
}

.parking-info {
  flex: 1;
}

.parking-name-row {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.parking-name {
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

.parking-type {
  margin-left: 10px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.parking-type.type-first {
  background: #e1f3d8;
  color: #67c23a;
}

.parking-type.type-second {
  background: #fdf6ec;
  color: #e6a23c;
}

.parking-status {
  font-size: 14px;
  padding: 2px 8px;
  border-radius: 4px;
}

.parking-status.active {
  background: #e1f3d8;
  color: #67c23a;
}

.parking-status.inactive {
  background: #fef0f0;
  color: #f56c6c;
}
</style>
<style>
.coupon-rules .input-item-text,
.coupon-rules input[type="text"],
.coupon-rules input[type="date"],
.coupon-rules select {
  height: 40px;
}
</style>